<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Breadcrumb -->
      <nav class="flex items-center gap-2 text-sm mb-6">
        <router-link to="/organisation" class="text-gray-500 hover:text-[#22282F] transition-colors">
          Organisation
        </router-link>
        <ChevronRight class="w-4 h-4 text-gray-400" />
        <router-link 
          :to="`/organisation/directions/${directionId}/departements`"
          class="text-gray-500 hover:text-[#22282F] transition-colors"
        >
          {{ direction?.nom }}
        </router-link>
        <ChevronRight class="w-4 h-4 text-gray-400" />
        <span class="text-[#22282F] font-medium">{{ departement?.nom }}</span>
      </nav>

      <!-- Hierarchy Info Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <!-- Direction Card -->
        <div class="bg-white border-2 border-[#22282F] rounded-xl p-4">
          <div class="flex items-center gap-3">
            <div class="p-2 bg-[#22282F] text-white rounded-lg">
              <Building2 class="w-5 h-5" />
            </div>
            <div>
              <p class="text-xs text-gray-500">Direction</p>
              <p class="font-semibold text-[#22282F]">{{ direction?.nom }}</p>
            </div>
          </div>
        </div>

        <!-- Département Card -->
        <div class="bg-gradient-to-r from-[#22282F] to-[#22282F]/80 text-white rounded-xl p-4">
          <div class="flex items-center gap-3">
            <div class="p-2 bg-white/10 rounded-lg">
              <Layers class="w-5 h-5" />
            </div>
            <div>
              <p class="text-xs text-white/70">Département</p>
              <p class="font-semibold">{{ departement?.nom }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-2xl font-bold text-[#22282F]">Services</h2>
          <p class="text-sm text-gray-500 mt-1">{{ departement?.nom }} • {{ departement?.code }}</p>
        </div>
        <button
          @click="openCreateModal"
          class="px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#22282F]/90 transition-colors flex items-center gap-2"
        >
          <Plus class="w-4 h-4" />
          Nouveau Service
        </button>
      </div>

      <!-- Search Bar -->
      <div class="mb-6 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div class="relative">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Rechercher un service par nom ou code..."
            class="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
          />
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-12 bg-white border border-gray-200 rounded-xl">
        <div class="w-8 h-8 border-4 border-gray-200 border-t-[#22282F] rounded-full animate-spin" />
      </div>

      <!-- Services Table -->
      <div v-else-if="filteredServices.length" class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <table class="w-full">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="text-left py-4 px-6 text-sm font-semibold text-[#22282F]">Service</th>
              <th class="text-left py-4 px-6 text-sm font-semibold text-[#22282F]">Code</th>
              <th class="text-center py-4 px-6 text-sm font-semibold text-[#22282F]">Utilisateurs</th>
              <th class="text-center py-4 px-6 text-sm font-semibold text-[#22282F]">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="service in filteredServices"
              :key="service.id"
              class="border-b border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <td class="py-4 px-6">
                <div class="flex items-center gap-3">
                  <div class="p-2 bg-gray-100 rounded-lg">
                    <Users class="w-4 h-4 text-gray-600" />
                  </div>
                  <span class="font-medium text-[#22282F]">{{ service.nom }}</span>
                </div>
              </td>
              <td class="py-4 px-6">
                <span class="text-sm text-gray-600">{{ service.code }}</span>
              </td>
              <td class="py-4 px-6 text-center">
                <span class="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700">
                  <Users class="w-3 h-3" />
                  {{ service.users_count || 0 }}
                </span>
              </td>
              <td class="py-4 px-6">
                <div class="flex items-center justify-center gap-2">
                  <button
                    @click="openEditModal(service)"
                    class="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Edit3 class="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    @click="deleteService(service.id)"
                    class="p-2 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 class="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-else class="bg-white border border-gray-200 rounded-xl p-12 text-center">
        <Users class="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 class="text-lg font-semibold text-[#22282F] mb-2">Aucun service trouvé</h3>
        <p class="text-sm text-gray-500">
          {{ searchQuery ? 'Essayez de modifier votre recherche' : 'Créez le premier service de ce département' }}
        </p>
      </div>

      <!-- Modal -->
      <div
        v-if="showModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
        @click.self="closeModal"
      >
        <div class="bg-white rounded-xl max-w-md w-full p-6">
          <h3 class="text-lg font-bold text-[#22282F] mb-4">
            {{ editingService ? 'Modifier le service' : 'Nouveau service' }}
          </h3>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-[#22282F] mb-2">Code</label>
              <input
                v-model="form.code"
                type="text"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                placeholder="ex: SRV-01"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-[#22282F] mb-2">Nom</label>
              <input
                v-model="form.nom"
                type="text"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                placeholder="ex: Service Ressources Humaines"
              />
            </div>
          </div>
          <div class="flex gap-3 mt-6">
            <button
              @click="closeModal"
              class="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              @click="saveService"
              class="flex-1 px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#22282F]/90 transition-colors"
            >
              {{ editingService ? 'Modifier' : 'Créer' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { Building2, Layers, Users, Plus, Edit3, Trash2, Search, ChevronRight } from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'

const toast = useToast()

interface Direction {
  id: number
  code: string
  nom: string
}

interface Departement {
  id: number
  code: string
  nom: string
  direction: Direction
}

interface Service {
  id: number
  code: string
  nom: string
  users_count?: number
  departement: Departement
}

const route = useRoute()
const directionId = ref(Number(route.params.directionId))
const departementId = ref(Number(route.params.departementId))

const loading = ref(false)
const direction = ref<Direction | null>(null)
const departement = ref<Departement | null>(null)
const services = ref<Service[]>([])
const searchQuery = ref('')
const showModal = ref(false)
const editingService = ref<Service | null>(null)
const form = ref({ code: '', nom: '' })

const filteredServices = computed(() => {
  if (!searchQuery.value) return services.value
  const query = searchQuery.value.toLowerCase()
  return services.value.filter(s =>
    s.nom.toLowerCase().includes(query) || s.code.toLowerCase().includes(query)
  )
})

const fetchDirection = async () => {
  try {
    direction.value = await apiFetch(`/api/admin/directions/${directionId.value}/`)
  } catch (error: any) {
    toast.error('Erreur', 'Impossible de charger la direction')
  }
}

const fetchDepartement = async () => {
  try {
    departement.value = await apiFetch(`/api/admin/departements/${departementId.value}/`)
  } catch (error: any) {
    toast.error('Erreur', 'Impossible de charger le département')
  }
}

const fetchServices = async () => {
  loading.value = true
  try {
    const allServices = await apiFetch('/api/admin/services/')
    services.value = allServices.filter((s: Service) => s.departement?.id === departementId.value)
  } catch (error: any) {
    toast.error('Erreur', 'Impossible de charger les services')
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  editingService.value = null
  form.value = { code: '', nom: '' }
  showModal.value = true
}

const openEditModal = (service: Service) => {
  editingService.value = service
  form.value = { code: service.code, nom: service.nom }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingService.value = null
}

const saveService = async () => {
  try {
    const payload = {
      ...form.value,
      departement_id: departementId.value
    }

    const url = editingService.value
      ? `/api/admin/services/${editingService.value.id}/update/`
      : '/api/admin/services/create/'

    await apiFetch(url, {
      method: editingService.value ? 'PUT' : 'POST',
      body: JSON.stringify(payload)
    })

    toast.success(
      editingService.value ? 'Service modifié' : 'Service créé',
      'L\'opération a réussi'
    )
    await fetchServices()
    closeModal()
  } catch (error: any) {
    toast.error('Erreur', error.message || 'Impossible de sauvegarder le service')
  }
}

const deleteService = async (id: number) => {
  try {
    await apiFetch(`/api/admin/services/${id}/delete/`, {
      method: 'DELETE'
    })

    toast.success('Service supprimé', 'Le service a été supprimé avec succès')
    await fetchServices()
  } catch (error: any) {
    toast.error('Erreur', error.message || 'Impossible de supprimer le service')
  }
}

onMounted(() => {
  fetchDirection()
  fetchDepartement()
  fetchServices()
})
</script>
